import { Component, DoCheck, HostBinding, Inject, OnDestroy, OnInit, inject } from '@angular/core';
import { AppService } from '../../../services/app.service';
import { Observable, Subject, takeUntil } from 'rxjs';
import { ActionButton } from '../../../models/app/action-button';
import { ComponentCanDeactivate } from '../../../interfaces/component-can-deactivate';
import { HttpErrorResponse } from '@angular/common/http';
import { ActivatedRoute } from '@angular/router';
import { Location } from '@angular/common';
import { MessageSeverity } from '../../../enums/message-severity';
import { ItemEnvelope } from '../../../models/net/envelope/base/item-envelope';
import { DeleteResult } from '../../../models/net/result/crud/delete-result';
import { UpdateResult } from '../../../models/net/result/crud/update-result';
import { ReadResult } from '../../../models/net/result/crud/read-result';
import { CreateResult } from '../../../models/net/result/crud/create-result';
import { ScrudgRevisionService } from '../../../services/base/scrudg-revision/scrudg-revision.service';
import { TelemetorComponent } from '../telemetor/telemetor.component';

@Component({
    selector: 'app-editor',
    templateUrl: './editor.component.html',
    styleUrls: ['./editor.component.css']
})

export class EditorComponent extends TelemetorComponent implements OnInit, DoCheck, OnDestroy, ComponentCanDeactivate {

    public itemId: number = 0;
    public revisionNumber: number | undefined;
    public itemEnvelope: ItemEnvelope | null = null;
    public itemEnvelopeSnapshot: ItemEnvelope | null = null;
    public isPendingChanges: boolean = false;
    public itemRoute: string = "";
    public searchRoute: string = "";
    public destroy: any = new Subject<void>();
    public actionButtons: ActionButton[] = [];
    public isRevisionMode: boolean = false;
    public subtitle: string = "";

    protected isRevisionListLoaded: boolean = false;

    @HostBinding("class.component-flex-vertical-fill") addClass = true;

    public constructor(public appService: AppService,
                       public itemService: ScrudgRevisionService,
                       public route: ActivatedRoute,
                       public location: Location,
                       @Inject("") itemRoute: string,
                       @Inject("") searchRoute: string) {
        super();

        this.itemRoute = itemRoute;
        this.searchRoute = searchRoute;

        this.appService.actionButtonClicked.pipe(takeUntil(this.destroy)).subscribe((actionName: string) => {

            switch (actionName) {

                case "save":

                    this.save();

                    break;

                case "undo":

                    this.undo();

                    break;

                case "delete":

                    this.delete();

                    break;

                default:

                    this.handleEditMode();

                    break;
            }
        });
    }

    public handleEditMode(): void { }

    public override ngOnInit(): void {

        const parameters = this.route.params;

        if (parameters != null) {

            parameters.subscribe((p) => {

                this.itemId = p["id"];

                if (this.itemId == 0) {

                    this.itemService.getBlankEnvelope().subscribe((r: ItemEnvelope | null) => {

                        if (r != null) {

                            if (r instanceof HttpErrorResponse) {

                                this.appService.showDialog(MessageSeverity.Error, "Error Creating " + this.itemEnvelope?.ItemTypeName, "There was an error creating new " + this.itemEnvelope?.ItemTypeName, "OK", "", true, true);

                            } else {

                                // setup actions
                                this.actionButtons.push(new ActionButton("SAVE CHANGES", "save", "save", "disabled", false));
                                this.actionButtons.push(new ActionButton("UNDO CHANGES", "undo", "undo", "disabled", false));
                                this.actionButtons.push(new ActionButton("DELETE", "delete", "delete", "disabled", false));
                                this.actionButtons.push(new ActionButton("EDIT MODE", "keyboard", "edit", "", true));
                                //this.actionButtons.push(new ActionButton("APP FEED", "", "", "", false));

                                this.initializeEnvelope(r);

                                this.appService.setActionsDataSource(this.actionButtons);
                            }
                        }
                    });

                } else {

                    this.revisionNumber = p["number"];

                    if (this.revisionNumber) {

                        this.appService.IsAppBusy = true;

                        this.itemService.readRevision(this.itemId, this.revisionNumber).subscribe((r: ReadResult | null) => {

                            if (r != null) {

                                if (r instanceof HttpErrorResponse) {

                                    this.appService.showDialog(MessageSeverity.Error, "Error Getting " + this.itemEnvelope?.ItemTypeName, "There was an error getting " + this.itemEnvelope?.ItemTypeName + " revision from the server", "OK", "", true, true);

                                } else {

                                    if (r.IsSuccess) {

                                        this.isRevisionMode = true;

                                        // setup actions
                                        this.actionButtons.push(new ActionButton("SAVE CHANGES", "save", "save", "disabled", true));
                                        this.actionButtons.push(new ActionButton("UNDO CHANGES", "undo", "undo", "disabled", true));
                                        this.actionButtons.push(new ActionButton("DELETE", "delete", "delete", "", true));
                                        this.actionButtons.push(new ActionButton("EDIT MODE", "keyboard", "edit", "", false));
                                        //this.actionButtons.push(new ActionButton("APP FEED", "", "", "", false));

                                        this.initializeEnvelope(r.ReadEnvelope);

                                        this.appService.setActionsDataSource(this.actionButtons);

                                        this.appService.IsAppBusy = false;

                                    } else {

                                        this.appService.IsAppBusy = false;
                                        this.appService.showDialog(MessageSeverity.Error, "Error Reading Item", "There was an error reading revision item as follows " + r.Message, "OK", "", true, true);
                                    }
                                }
                            }
                        });

                    } else {

                        this.appService.IsAppBusy = true;

                        this.itemService.read(this.itemId).subscribe((r: ReadResult | null) => {

                            if (r != null) {

                                if (r instanceof HttpErrorResponse) {

                                    this.appService.IsAppBusy = false;
                                    this.appService.showDialog(MessageSeverity.Error, "Error Getting " + this.itemEnvelope?.ItemTypeName, "There was an error getting " + this.itemEnvelope?.ItemTypeName + " from the server", "OK", "", true, true);

                                } else {

                                    if (r.IsSuccess) {

                                        // setup actions
                                        this.actionButtons.push(new ActionButton("SAVE CHANGES", "save", "save", "disabled", false));
                                        this.actionButtons.push(new ActionButton("UNDO CHANGES", "undo", "undo", "disabled", false));
                                        this.actionButtons.push(new ActionButton("DELETE", "delete", "delete", "disabled", false));
                                        this.actionButtons.push(new ActionButton("EDIT MODE", "keyboard", "edit", "", true));
                                        //this.actionButtons.push(new ActionButton("APP FEED", "", "", "", false));

                                        this.initializeEnvelope(r.ReadEnvelope);

                                        this.appService.setActionsDataSource(this.actionButtons);

                                        this.appService.IsAppBusy = false;

                                    } else {

                                        this.appService.IsAppBusy = false;
                                        this.appService.showDialog(MessageSeverity.Error, "Error Reading Item", "There was an error reading item as follows " + r.Message, "OK", "", true, true);
                                    }
                                }
                            }
                        });
                    }
                }
            });
        }

        this.emitTelemetrigram("load");
    }

    public initializeEnvelope(envelope: ItemEnvelope | null): void {

        if (this.revisionNumber) {

            if (envelope!.ModifiedOnUTC) {

                this.subtitle = "REVISION DETAILS: Revision: " + this.revisionNumber.toString() + " performed on " + envelope!.ModifiedOnUTC + " by " + envelope!.ModifyUserUID;

            } else {

                if (envelope?.CreatedOnUTC) {

                    this.subtitle = "REVISION DETAILS: Created on " + envelope.CreatedOnUTC.toString() + " by " + envelope!.CreateUserUID;
                }
            }

        } else {

            this.subtitle = "Edit Item";
        }
    }

    public save(): void {

        if (this.itemId <= 0) {

            if (this.itemEnvelope != null) {

                this.appService.IsAppBusy = true;

                this.itemService.create(this.itemEnvelope).subscribe((r: CreateResult | null) => {

                    if (r != null) {

                        if (r instanceof HttpErrorResponse) {

                            this.appService.IsAppBusy = false;
                            this.appService.showDialog(MessageSeverity.Error, "Error Saving " + this.itemEnvelope?.ItemTypeName, "There was an error saving item " + this.itemEnvelope?.ItemName, "OK", "", true, true);

                        } else {

                            this.initializeEnvelope(r.CreatedEnvelope);
                            this.itemId = this.itemEnvelope?.ItemId ?? 0;
                            this.isPendingChanges = false;
                            this.appService.actionsDatasource[0].disabled = "disabled";
                            this.appService.actionsDatasource[1].disabled = "disabled";
                            this.appService.IsAppBusy = false;
                            this.location.replaceState(this.itemRoute + "/" + this.itemEnvelope?.ItemId.toString());

                            this.appService.IsAppBusy = false;
                            this.appService.addAppFeedMessage(MessageSeverity.Success, "New " + this.itemEnvelope?.ItemTypeName + " created", true);
                        }
                    }
                });
            }

        } else {

            if (this.itemEnvelope != null) {

                this.appService.IsAppBusy = true;

                this.itemService.update(this.itemEnvelope).subscribe((r: UpdateResult | null) => {

                    if (r != null) {

                        if (r instanceof HttpErrorResponse) {

                            this.appService.IsAppBusy = false;
                            this.appService.showDialog(MessageSeverity.Error, "Error Saving " + this.itemEnvelope?.ItemTypeName, "There was an error saving item " + this.itemEnvelope?.ItemName, "OK", "", true, true);

                        } else {

                            if (r.IsSuccess) {

                                this.initializeEnvelope(r.UpdatedEnvelope);
                                this.itemId = this.itemEnvelope?.ItemId ?? 0;
                                this.isPendingChanges = false;
                                this.appService.actionsDatasource[0].disabled = "disabled";
                                this.appService.actionsDatasource[1].disabled = "disabled";

                                this.appService.IsAppBusy = false;
                                this.appService.addAppFeedMessage(MessageSeverity.Success, "Updated " + this.itemEnvelope?.ItemTypeName + " " + this.itemEnvelope?.ItemName, true);

                            } else {

                                this.appService.IsAppBusy = false;
                                this.appService.showDialog(MessageSeverity.Error, "Error Updating Item", "There was an error updating item as follows " + r.Message, "OK", "", true, true);
                            }
                        }
                    }
                });
            }
        }
    }

    public undo(): void {

        this.appService.showDialog(MessageSeverity.Warning, "Confirm Undo", "Are you sure you want to undo your changes ? Any changes you have made to this" + this.itemEnvelope?.ItemTypeName + " will be lost.  Are you sure you want to undo ?", "YES I AM SURE", "CANCEL UNDO", false, false).subscribe((response: string) => {

            if (response == "YES I AM SURE") {

                this.initializeEnvelope(this.itemEnvelopeSnapshot);
                this.isPendingChanges = false;
                this.appService.actionsDatasource[0].disabled = "disabled";
                this.appService.actionsDatasource[1].disabled = "disabled";
            }
        });
    }

    public clone(): void {

        const s: string = "";
    }

    public delete(): void {

        this.appService.showDialog(MessageSeverity.Warning, "Confirm Deletion", "You are about to delete " + this.itemEnvelope?.ItemTypeName + " " + this.itemEnvelope?.ItemName + ", click DELETE to continue or CANCEL to stay on this item", "DELETE", "CANCEL", false, false).subscribe((r: string) => {

            if (r == "DELETE") {

                this.itemService.delete(this.itemEnvelope?.ItemId ?? 0).subscribe((r: DeleteResult | null) => {

                    if (r != null) {

                        if (r instanceof HttpErrorResponse) {

                            this.appService.IsAppBusy = false;
                            this.appService.showDialog(MessageSeverity.Error, "Error Deleting " + this.itemEnvelope?.ItemTypeName, "An error occurred deleting " + this.itemEnvelope?.ItemTypeName + " " + this.itemEnvelope?.ItemName, "OK", "", true, true);

                        } else {

                            if (r.IsSuccess) {

                                this.appService.IsAppBusy = false;
                                this.appService.navigateTo(this.searchRoute, "");
                                this.appService.addAppFeedMessage(MessageSeverity.Info, "Successfully deleted " + this.itemEnvelope?.ItemTypeName + " " + this.itemEnvelope?.ItemName, true);

                            } else {

                                this.appService.IsAppBusy = false;
                                this.appService.showDialog(MessageSeverity.Error, "Error Deleting Item", "There was an error deleting item as follows " + r.Message, "OK", "", true, true);
                            }
                        }
                    }
                });
            }
        });
    }

    public canDeactivate(): boolean | Observable<boolean> {

        let result: boolean = !this.isPendingChanges;

        return result;
    }

    public ngDoCheck(): void {

        if (!this.revisionNumber) { // actionsDatasource[0] and actionsDatasource[1] are 'save' and 'undo' are not available if the current mode is revision mode 

            if (JSON.stringify(this.itemEnvelope) != JSON.stringify(this.itemEnvelopeSnapshot)) {

                this.isPendingChanges = true;

                if (this.appService.actionsDatasource.length > 1) {

                    this.appService.actionsDatasource[0].disabled = "";
                    this.appService.actionsDatasource[1].disabled = "";
                }

            } else {

                this.isPendingChanges = false;

                if (this.appService.actionsDatasource.length > 1) {

                    this.appService.actionsDatasource[0].disabled = "disabled";
                    this.appService.actionsDatasource[1].disabled = "disabled";
                }
            }
        }
    }

    public ngOnDestroy() {

        this.appService.setActionsDataSource([]);
        this.destroy.next();
        this.destroy.complete();
    }
}
